import { Grid, Paper, Typography } from '@material-ui/core';
import { isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { IUser } from '~/src/dash/api/console-api';
import {
  IIASFirstAndThirdPartyRequestBody,
  queryFirstAndThirdPartyIasData,
} from '~/src/dash/api/ias-wrapper-api/ias-wrapper-api';
import { convertLargeNumbersToRoundedString } from '~/src/dash/utils/basic';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { IASMeasurementTrackEvent } from '~/src/dash/views/measurement/common/consts';
import { IMeasurementQueryParamsNoComp } from '~/src/dash/views/measurement/common/types';
import {
  IMeasurementChart,
  IMeasurementTableColumn,
  IMeasurementTableGroupBy,
} from '~/src/dash/views/measurement/config';
import { ISnackbarState } from '~/src/dash/views/snackbar-view';

const findEndIndexOfNameString = (name: string) => {
  const length = name.length;
  let endIndex = length - 1;
  if (name[length - 1] === ')') {
    for (let i = length - 2; i--; i > 0) {
      if (name[i] === '(') {
        endIndex = i;
      }
    }
  }
  return endIndex;
};

const removeIDFromEndOfName = (name: string) => {
  const endIndex = findEndIndexOfNameString(name);
  return name.slice(0, endIndex - 1);
};

// returns the id inside of the ending parenthesis in the name ex. 'AdvertiserX (007)' will return '007'
const extractIDFromEndOfName = (name: string) => {
  const endIndex = findEndIndexOfNameString(name);
  return name.slice(endIndex + 1, name.length - 1);
};

interface IMeasurementChartData {
  name: string;
  ID: string;
  Value?: number;
  Rate?: number;
}

const BarLabel = ({ x, y, width, height, value, isRate }: Record<string, any>) => {
  const valueString = `${convertLargeNumbersToRoundedString(value)}${isRate ? '%' : ''}`;
  return (
    <text
      x={x + width + 4}
      y={y + height / 2 + 5}
      fill={'black'}
      textAnchor='start'
      style={{ fontSize: 12 }}
    >
      {valueString}
    </text>
  );
};

const renderChart = (
  chartData: IMeasurementChartData[],
  colorBank: string[],
  isRate: boolean,
  handleClick: (name: string, id: string, isFromChart: boolean) => void,
) => {
  const valueKey = isRate ? 'Rate' : 'Value';
  const isFromChart = true;
  return (
    <BarChart
      data={chartData}
      layout='vertical'
      height={400}
      margin={{ top: 16, right: 16, bottom: 16, left: 64 }}
      style={{ width: '100%' }}
    >
      <XAxis
        type='number'
        tick={{ fontSize: 12, fill: 'black', textAnchor: 'end' }}
        tickFormatter={convertLargeNumbersToRoundedString}
      />
      <YAxis
        type='category'
        dataKey='name'
        width={120}
        tick={{
          fontSize: 12,
          fill: 'black',
          cursor: 'pointer',
          textOverflow: 'ellipsis',
        }}
        onClick={(props) =>
          handleClick(props.value, extractIDFromEndOfName(props.value), isFromChart)
        }
        tickFormatter={removeIDFromEndOfName}
      />
      <Tooltip formatter={(value) => value && `${value.toLocaleString()}${isRate ? '%' : ''}`} />
      <Bar dataKey={valueKey} barSize={32}>
        {chartData.map((item, index) => (
          <Cell
            key={`cell-${index}`}
            fill={colorBank[index % colorBank.length]}
            style={{ cursor: 'pointer' }}
            onClick={() => handleClick(item.name, item.ID, isFromChart)}
          />
        ))}
        <LabelList
          dataKey={valueKey}
          // @ts-ignore
          content={<BarLabel isRate={isRate} />}
          position='right'
        />
      </Bar>
    </BarChart>
  );
};

const extractLabelFromDataKey = (key: string, columns: IMeasurementTableColumn[]) => {
  const result = columns.find(({ dataKey }) => dataKey === key);
  return result ? result.label : '';
};

const isPercentRenderType = (key: string, columns: IMeasurementTableColumn[]): boolean => {
  const result = columns.find(({ dataKey }) => dataKey === key);
  return result ? result.renderType === 'percent' : false;
};

const extractChartDataFromData = (
  data: Array<Record<string, any>>,
  dataKey: string,
  nameKey: string,
  columns: IMeasurementTableColumn[],
) => {
  const chartData = [] as IMeasurementChartData[];
  const valueKey = isPercentRenderType(dataKey, columns) ? 'Rate' : 'Value';
  data.forEach((item) => {
    chartData.push({
      name: `${item[nameKey]} (${item.ID})`,
      ID: item.ID,
      [valueKey]: item[dataKey],
    });
  });
  return chartData;
};

interface IMeasurementChartProps {
  classes: Record<string, string>;
  colors: Record<string, string>;
  queryParams: Partial<IMeasurementQueryParamsNoComp>;
  chartInfo: IMeasurementChart;
  tableColumns: IMeasurementTableColumn[];
  currentGroupBy: IMeasurementTableGroupBy;
  user: IUser;
  setSnackbar: (snackbar: ISnackbarState) => void;
  currentPage: number;
  currentPageSize: number;
  currentNameSearch: string;
  currentIDSearch: string;
  currentTableOrderDirection: string;
  handleChartItemClick: (name: string, id: string, isFromChart: boolean) => void;
  chartDataKey: string;
}

export const MeasurementChart = ({
  classes,
  colors,
  queryParams,
  chartInfo,
  tableColumns,
  currentGroupBy,
  setSnackbar,
  user,
  currentPage,
  currentPageSize,
  currentNameSearch,
  currentIDSearch,
  currentTableOrderDirection,
  handleChartItemClick,
  chartDataKey,
}: IMeasurementChartProps) => {
  const isMounted = useRef(false);
  const [loadState, setLoadState] = useState(LoadingStateLoading);
  // Notes: These are held in state to ensure charts display without error
  const [params, setParams] = useState(queryParams);
  const [info, setInfo] = useState(chartInfo);
  const [data, setData] = useState<any>(null);
  const [chartData, setChartData] = useState<IMeasurementChartData[]>([]);

  const colorBank = Object.values(colors);

  const { graph } = info;

  const {
    startDate,
    endDate,
    // TODO: change comp dates when this feature is implemented
    // compStartDate,
    // compEndDate,
    timezone,
    groupingNumber,
    deviceTypes,
    teamID,
    anIDs,
    publisherID,
    filters,
    groupByKey,
    advertiserIDs,
    orderIDs,
    lineItemIDs,
    creativeIDs,
    adUnitIDs,
  } = params;

  useEffect(() => {
    setLoadState(LoadingStateLoading);
  }, []);

  // once we receive the params prop, we set it to state
  useEffect(() => {
    setTimeout(() => {
      setParams(queryParams);
    }, 1000);
  }, [queryParams]);

  // once we receive the chart info props, we set it to state
  useEffect(() => {
    setInfo(chartInfo);
  }, [chartInfo]);

  // whenever the params or info changes, we run the api call
  useEffect(() => {
    if (!isEmpty(params) && !isEmpty(info)) {
      runAggregateQuery();
    }
  }, [
    params,
    info,
    currentPage,
    currentPageSize,
    currentNameSearch,
    currentIDSearch,
    currentTableOrderDirection,
    chartDataKey,
  ]);

  // whenever the data or chart info changes, we set the chart data
  useEffect(() => {
    if (isMounted.current && data && data.length > 0) {
      if (chartDataKey) {
        setChartData(
          extractChartDataFromData(data[0], chartDataKey, currentGroupBy.dataKey, tableColumns),
        );
      }
    } else {
      isMounted.current = true;
    }
  }, [data, chartInfo]);

  // whenever the chart data changes, we set the correct load state
  useEffect(() => {
    if (isMounted.current && chartData !== undefined) {
      setLoadState(chartData && chartData.length > 0 ? LoadingStateHasData : LoadingStateLoading);
    }
  }, [chartData]);

  const runAggregateQuery = (): void => {
    // reset load state
    setLoadState(LoadingStateLoading);
    // query 1 (normal calendar)
    // const query_duration_days = moment(endDate).diff(startDate, 'days') + 1;
    // query 2 (compare calendar)
    // const comp_query_duration_days = compareCalendar.endDate.diff(compareCalendar.startDate, 'days') + 1;

    const body1 = {
      GraphID: graph,
      StartDate: startDate,
      EndDate: endDate,
      Timezone: timezone,
      GroupingNumber: groupingNumber,
      DeviceTypes: deviceTypes,
      TeamID: teamID,
      AnIDs: anIDs,
      Filters: filters,
      AdvertiserIDs: advertiserIDs,
      OrderIDs: orderIDs,
      LineItemIDs: lineItemIDs,
      CreativeIDs: creativeIDs,
      AdUnitIDs: adUnitIDs,
      ReportOrderByKey: chartDataKey,
      ReportOrderBy: currentTableOrderDirection || 'desc',
      GroupByKey: groupByKey,
      Page: currentPage,
      PageSize: currentPageSize,
      NameSearch: currentNameSearch,
      IDSearch: currentIDSearch,
    } as IIASFirstAndThirdPartyRequestBody;
    // const body2 = {
    //   GraphID: graph_id,
    //   StartDate: comp_start_date,
    //   EndDate: comp_end_date,
    //   Timezone: cfg.timezone,
    //   GroupingNumber: groupingNumber,
    //   DeviceTypes: combinedDeviceTypes,
    //   TeamID: teamID,
    //   AnIDs: anIDs,
    // } as IIASFirstAndThirdPartyRequestBody;

    /** * Main Graph Queries ***/
    const queries = [
      queryFirstAndThirdPartyIasData(body1, { publisher_id: publisherID }),
      // queryFirstAndThirdPartyIasData(body2, { publisher_id: publisherID }),
    ];

    // Runs the queries together in Promise All
    const queryStartTime = moment().unix();
    const queryDurationDays = moment(endDate).diff(moment(startDate), 'days') + 1;
    Promise.all(queries)
      .then((data: any[][]) => {
        const queryDuration = moment().unix() - queryStartTime;
        let queryFailed = false;

        // if the call is successful but there is no data
        if (!data[0][0].rows) {
          queryFailed = true;
          unstable_batchedUpdates(() => {
            setLoadState(LoadingStateNoData);
            setSnackbar({
              message: `We are not tracking any measurement data here...\nPlease adjust the filters / date range combination`,
              warning: true,
              error: false,
            });
          });
          return;
        }

        const data1 = [] as any;
        data1.push(data[0][0].rows);
        // data1.push(data[1][0].rows);

        // check the data to make sure it's there, otherwise show an error msg
        if (!data1[0].rows) {
          if (data1[0].length === 0) {
            queryFailed = true;
            setLoadState(LoadingStateNoData);
            // if there is no compare data (query went wrong or publisher is new), reload the graph without compare
            // } else if (isComparedToEnabled && (!data1[1] || (data1[1] && data1[1].length === 0))) {
            //   queryFailed = true;
            //   unstable_batchedUpdates(() => {
            //     setIsComparedToEnabled(false);
            //     setLoadState(LoadingStateNoData);
            //     setSnackbar({
            //       message: 'There is no compare data available yet for this period. The dashboard will reload without compare mode enabled.',
            //       error: false,
            //       warning: false,
            //     });
            //   });
            // check max date queries, if they didn't return a date, try turning off mergeLiveData and running again
          }
        }
        if (queryFailed) {
          // track events for mixpanel
          IASMeasurementTrackEvent(user, {
            eventTitle: 'IAS Measurement Drill Dashboard - Run Chart Agg Query',
            graphID: +graph.replace(/\D/g, ''),
            startDate,
            endDate,
            // isComparedToEnabled ? comp_start_date : '',
            // isComparedToEnabled ? comp_end_date : '',
            timezone,
            queryDuration,
            queryFailed,
            queryDurationDays,
            // isComparedToEnabled ? comp_query_duration_days : 0,
            // isComparedToEnabled,
          });
          return;
        }

        // track events for mixpanel
        IASMeasurementTrackEvent(user, {
          eventTitle: 'IAS Measurement Drill Dashboard - Run Chart Agg Query',
          graphID: +graph.replace(/\D/g, ''),
          startDate,
          endDate,
          // isComparedToEnabled ? comp_start_date : '',
          // isComparedToEnabled ? comp_end_date : '',
          timezone,
          queryDuration,
          queryFailed,
          queryDurationDays,
          // isComparedToEnabled ? comp_query_duration_days : 0,
          // isComparedToEnabled,
        });

        // );

        unstable_batchedUpdates(() => {
          setData([
            data1[0] ? data1[0] : [],
            // !!data1[1] ? data1[1] : [],
          ]);
          setLoadState(LoadingStateHasData);
        });
      })
      .catch(() => {
        // track events for mixpanel
        const queryDuration = moment().unix() - queryStartTime;
        IASMeasurementTrackEvent(user, {
          eventTitle: 'IAS Measurement Drill Dashboard - Run Chart Agg Query',
          graphID: +graph.replace(/\D/g, ''),
          startDate,
          endDate,
          // isComparedToEnabled ? comp_start_date : '',
          // isComparedToEnabled ? comp_end_date : '',
          timezone,
          queryDuration,
          queryFailed: true,
          queryDurationDays,
          // isComparedToEnabled ? comp_query_duration_days : 0,
          // isComparedToEnabled,
        });
        setLoadState(LoadingStateNoData);
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateNoData);
          setSnackbar({
            message: 'Something went wrong: Please try again.',
            error: true,
            warning: false,
          });
        });
      });
  };

  const title = chartDataKey ? extractLabelFromDataKey(chartDataKey, tableColumns) : '';
  const isRate = chartDataKey ? isPercentRenderType(chartDataKey, tableColumns) : false;

  return (
    <div>
      <Paper className={classes.paper}>
        <Grid container alignItems='center' spacing={1}>
          <Grid item container justify='center' xs={12}>
            <Typography className={classes.chartName}>{title}</Typography>
          </Grid>

          {loadState === LoadingStateHasData ? (
            <ResponsiveContainer width='99%' height={400}>
              {/* must be a render function due to the recharts js library */}
              {renderChart(chartData, colorBank, isRate, handleChartItemClick)}
            </ResponsiveContainer>
          ) : (
            <Grid container alignItems='center' direction='column'>
              <Grid
                item
                style={{
                  width: '99%',
                  height: 400,
                  textAlign: 'center',
                }}
              >
                <Typography style={{ marginTop: 150 }}>
                  {loadState === LoadingStateNoData
                    ? 'No data to display for the selected period or filters'
                    : 'Loading query...'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Paper>
    </div>
  );
};
